import { AGENT_FLAG, REPORTS_FLAG, type SidebarView } from '@forecastify/shared';
import type { MessageKey } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ F-2 · the tour, as data: chapters of steps, what each points at, to whom.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The script the product owner approved (plan page, *The tour script v2*, 9 Oct 2026): every page is
 * walked control by control, in chapters, and the tour ACTS — it clicks a tab, switches a view,
 * opens a task or a form — before it points.
 *
 * ⚠️⚠️ **EVERY CONDITION READS WHAT THE PERSON MAY DO, NEVER WHAT THEIR ROLE IS CALLED.** Admins edit
 * roles and make their own. `TourContext` carries the effective permissions the server enforces, the
 * two leave entitlements the server works out, the plan's flags and the demo flag — nothing else, so
 * a role name has nowhere to come from. `tour-steps.test.ts` also refuses one in this file.
 *
 * ⚠️ **What a missing target means is the step's to say.** `center` draws the card in the middle —
 * a sidebar row on a phone, a screen that has nothing to point at. `skip` moves on — a control the
 * screen draws only for some people, or a row that is not there to open. That is what keeps the tour
 * and the screen from disagreeing, even about a custom role.
 *
 * ⚠️⚠️ **EVERY STEP STANDS ON ITS OWN.** A step names its page and everything it clicks to get there
 * from that page — the task it opens, the tab, the view — so the tour can arrive at it from ANY step:
 * Next, Previous, Skip this section, or a step skipped on the way. Steps used to lean on whatever the
 * step before them had left behind, and every door but Next arrived somewhere else: Previous from a
 * step that opened a form left the form over the step before it, Previous across a page boundary
 * stayed on the later page, and skipping a section passed over the step that put the list back.
 *
 * - **The page**: a rule without `route` is on the page of the rule before it, resolved here, once,
 *   over the whole script — so a built step carries its page even when the step that named it was
 *   left out for this person. Only the steps that point at the shell itself (the banner, the
 *   workspace menu) have none, and they need none.
 * - **The clicks are idempotent**: a click that would undo itself — a view switch's menu that is
 *   already open, a Home section already open — says when not to happen (`unless`, `ifPresent`).
 * - **What opens a surface says so** (`opens`), and the engine (`tour-driver.ts`) closes it as
 *   soon as the tour is on a step that does not open it too — by any door, the X included. The tour
 *   never submits a form.
 */

export interface TourContext {
  readonly has: (action: string) => boolean;
  readonly scopeOf: (action: string) => string;
  readonly entitlements: SidebarView['entitlements'];
  readonly features: SidebarView['features'];
  readonly demo: boolean;
  /** The project the project chapter opens, when there is one. */
  readonly projectId: string | null;
}

export type TourSide = 'top' | 'bottom' | 'left' | 'right';

/**
 * Something the tour does before it points. Every one is a click a person could have made.
 *
 * - `click`: wait for the element, then click it. `required` waits longer — for a row that has to
 *   load — and skips the step when it never comes; otherwise a missing element is briefly looked for
 *   and simply not clicked. `ifPresent` looks once and moves on — for a click that only matters in
 *   one state, like opening a Home section that may already be open.
 * - `unless`: not clicked when this selector is already on screen — a menu that is already open.
 * - `opens`: the click opens a modal surface — a task, a form — named so that a step needing the
 *   same one finds it already open rather than opening it again, and a step that does not need it
 *   has it closed first. Nothing else ever closes what the tour opened.
 */
export interface TourAction {
  readonly click: string;
  readonly required?: boolean;
  readonly ifPresent?: boolean;
  readonly unless?: string;
  readonly opens?: string;
}

export type ChapterId =
  | 'demo'
  | 'settings'
  | 'home'
  | 'tasks'
  | 'task'
  | 'projects'
  | 'time'
  | 'requests'
  | 'contracts'
  | 'people'
  | 'reports'
  | 'dashboards'
  | 'agent'
  | 'end';

export interface TourStep {
  readonly id: string;
  readonly chapter: ChapterId;
  /**
   * Workspace-relative: the page the step is on. `null` only for a step about the shell — the
   * banner, the workspace menu — which is the same on every page.
   */
  readonly route: string | null;
  readonly before: readonly TourAction[];
  /** A CSS selector, or `null` for a centred card. */
  readonly target: string | null;
  readonly side: TourSide;
  readonly ifMissing: 'center' | 'skip';
  readonly title: MessageKey;
  readonly body: MessageKey;
}

interface Chapter {
  readonly id: ChapterId;
  readonly title: MessageKey;
  readonly when: (context: TourContext) => boolean;
}

interface StepRule {
  readonly id: string;
  readonly chapter: ChapterId;
  /** Absent: the page of the rule before this one (see the header). */
  readonly route?: string | ((context: TourContext) => string | null);
  readonly before?: readonly TourAction[];
  readonly target: string | null;
  readonly side?: TourSide;
  readonly ifMissing?: 'center' | 'skip';
  readonly when?: (context: TourContext) => boolean;
}

const tour = (name: string): string => `[data-tour="${name}"]`;
const click = (selector: string, required = false): TourAction => ({ click: selector, required });

/**
 * Open a list's view switch and pick a view — two clicks a person would make. ⚠️ The switch is not
 * pressed when its menu is already open: a second press would shut it, and the pick would find
 * nothing to click.
 */
const view = (value: string): TourAction[] => [
  { click: tour('view-switch'), unless: tour(`view-${value}`) },
  click(tour(`view-${value}`)),
];

/**
 * Where a Home section remembers being shut. ⚠️ That is the person's choice, and the tour opens the
 * sections it points at — so `Tour` remembers these when it starts and puts them back when it ends.
 */
export const TOUR_STORAGE_PREFIX = 'forecastify.home.';

const TASKS_SCREEN = '[data-screen="tasks"], [data-screen="my-tasks"]';
const PROJECT = '[data-screen="project"]';
const projectTab = (value: string): TourAction => click(`${PROJECT} [id$="-tab-${value}"]`, true);

/** The list, then its first row: the task the task chapter walks. */
const OPEN_TASK: readonly TourAction[] = [
  ...view('list'),
  {
    click: '[data-screen="tasks"] tbody tr, [data-screen="my-tasks"] tbody tr',
    required: true,
    opens: 'task',
  },
];

const REQUESTS = '[data-screen="requests"]';
const LEAVE_TAB = click(`${REQUESTS} [id$="-tab-leave"]`);
/** The leave tab, then its New button: the form the leave steps walk. */
const OPEN_LEAVE_FORM: readonly TourAction[] = [
  LEAVE_TAB,
  { click: tour('list-primary'), required: true, opens: 'leave-form' },
];

/** The builder's measure, picked — the dimension section is dimmed until there is one. */
const HOURS: readonly TourAction[] = [
  click(tour('report-metrics-add')),
  click(tour('metric-hours')),
];

export const CHAPTERS: readonly Chapter[] = [
  { id: 'demo', title: 'tour.chapter.demo', when: (c) => c.demo },
  { id: 'settings', title: 'tour.chapter.settings', when: (c) => c.has('settings.manage') },
  { id: 'home', title: 'tour.chapter.home', when: () => true },
  { id: 'tasks', title: 'tour.chapter.tasks', when: (c) => c.has('task.view') },
  { id: 'task', title: 'tour.chapter.task', when: (c) => c.has('task.view') },
  {
    id: 'projects',
    title: 'tour.chapter.projects',
    when: (c) => c.has('project.view') && c.projectId !== null,
  },
  {
    id: 'time',
    title: 'tour.chapter.time',
    /* 26 Oct 2026 · and only for somebody who logs time, as the sidebar row. */
    when: (c) => c.has('worklog.create') && c.entitlements.logsTime,
  },
  {
    id: 'requests',
    title: 'tour.chapter.requests',
    when: (c) => c.entitlements.leaveRequest || c.entitlements.leaveDecide,
  },
  /* 22 Oct 2026 · ⚠️ **THE SIDEBAR'S OWN ENTITLEMENTS, NOT THE READ PERMISSIONS BEHIND THEM.** The
     workspace menu rows, *Riportok* and each dashboard became a grant of their own (`0235`), and
     the tour kept asking the older question — so a basic employee was walked through screens their
     sidebar does not offer. One answer for both: what the server put in `entitlements`. */
  {
    id: 'contracts',
    title: 'tour.chapter.contracts',
    when: (c) =>
      c.has('contract.view') && (c.entitlements.menuContracts || c.entitlements.menuInvoices),
  },
  {
    id: 'people',
    title: 'tour.chapter.people',
    when: (c) => c.has('people.view') && c.entitlements.menuPeople,
  },
  {
    id: 'reports',
    title: 'tour.chapter.reports',
    when: (c) => c.entitlements.reports && c.features[REPORTS_FLAG] !== false,
  },
  {
    id: 'dashboards',
    title: 'tour.chapter.dashboards',
    when: (c) =>
      c.entitlements.dashboardLogging ||
      c.entitlements.dashboardResource ||
      c.entitlements.dashboardLeave,
  },
  { id: 'agent', title: 'tour.chapter.agent', when: (c) => c.features[AGENT_FLAG] === true },
  { id: 'end', title: 'tour.chapter.end', when: () => true },
];

const RULES: readonly StepRule[] = [
  /* ── demo ───────────────────────────────────────────────────────────────────────────────── */
  { id: 'demo', chapter: 'demo', target: tour('banner') },

  /* ── workspace settings, setting by setting ─────────────────────────────────────────────── */
  { id: 'settingsMenu', chapter: 'settings', target: tour('workspace-menu'), side: 'right' },
  {
    id: 'settingsIdentity',
    chapter: 'settings',
    route: '/settings',
    target: tour('settings-identity'),
  },
  { id: 'settingsDefaults', chapter: 'settings', target: tour('settings-defaults') },
  { id: 'settingsCountry', chapter: 'settings', target: tour('settings-country') },
  {
    id: 'settingsPermissions',
    chapter: 'settings',
    route: '/settings/permissions',
    target: '[data-screen="permissions"]',
  },
  {
    id: 'settingsLevel',
    chapter: 'settings',
    before: [click('[data-screen="permissions"] [role="button"]', true)],
    target: '[data-action]',
    ifMissing: 'skip',
  },
  {
    id: 'settingsStatuses',
    chapter: 'settings',
    route: '/settings/tasks',
    before: [click('[id$="-tab-statuses"]')],
    target: tour('settings-table'),
  },
  {
    id: 'settingsPriorities',
    chapter: 'settings',
    before: [click('[id$="-tab-priorities"]')],
    target: tour('settings-table'),
  },
  {
    id: 'settingsProjectRoles',
    chapter: 'settings',
    route: '/settings/project-roles',
    target: tour('settings-table'),
  },
  {
    id: 'settingsMetafields',
    chapter: 'settings',
    route: '/settings/metafields',
    target: '[data-screen="metafields"]',
  },
  {
    id: 'settingsSkills',
    chapter: 'settings',
    route: '/settings/skills',
    target: '[data-screen="settings-skills"]',
  },
  {
    id: 'settingsSchedules',
    chapter: 'settings',
    route: '/settings/schedules',
    before: [click('[id$="-tab-templates"]')],
    target: tour('settings-table'),
  },
  {
    id: 'settingsAssignments',
    chapter: 'settings',
    before: [click('[id$="-tab-assignments"]')],
    target: tour('settings-table'),
  },
  {
    id: 'settingsRates',
    chapter: 'settings',
    route: '/settings/rates',
    target: tour('settings-table'),
  },
  {
    id: 'settingsCurrency',
    chapter: 'settings',
    route: '/settings/currency',
    target: tour('settings-table'),
  },
  {
    id: 'settingsLeaveTypes',
    chapter: 'settings',
    route: '/settings/leave',
    before: [click('[id$="-tab-types"]')],
    target: tour('settings-table'),
  },
  {
    id: 'settingsQuotas',
    chapter: 'settings',
    before: [click('[id$="-tab-quotas"]')],
    target: '[data-screen="settings-leave"]',
  },
  {
    id: 'settingsOvertime',
    chapter: 'settings',
    route: '/settings/overtime',
    target: tour('settings-table'),
  },
  {
    id: 'settingsHolidays',
    chapter: 'settings',
    route: '/settings/holidays',
    target: tour('settings-secondary'),
  },
  {
    id: 'settingsWorklog',
    chapter: 'settings',
    route: '/settings/worklog',
    target: tour('settings-worklog-unit'),
  },
  { id: 'settingsWorklogLock', chapter: 'settings', target: tour('settings-worklog-backdate') },
  {
    id: 'settingsIntegrations',
    chapter: 'settings',
    route: '/settings/integrations',
    target: '[data-screen="settings-integrations"]',
  },
  {
    id: 'settingsData',
    chapter: 'settings',
    route: '/settings/data',
    target: tour('settings-export'),
  },
  {
    id: 'settingsBilling',
    chapter: 'settings',
    route: '/settings/billing',
    target: '[data-screen="billing"]',
    when: (c) => c.has('billing.manage'),
  },

  /* ── home ───────────────────────────────────────────────────────────────────────────────── */
  /* 16 Oct 2026 · the reworked homescreen: the question box first, then the cards. The cards no
     longer collapse, and Leave and Overtime only exist when they have something to show — so
     `skip` when one is missing, as before. */
  {
    id: 'homeAgent',
    chapter: 'home',
    route: '/home',
    target: tour('home-composer'),
    when: (c) => c.features[AGENT_FLAG] === true,
    ifMissing: 'skip',
  },
  {
    id: 'homeLog',
    chapter: 'home',
    route: '/home',
    target: tour('home-log'),
    ifMissing: 'skip',
  },
  {
    id: 'homeAllocations',
    chapter: 'home',
    target: tour('home-allocations'),
    ifMissing: 'skip',
  },
  {
    id: 'homeLeave',
    chapter: 'home',
    target: tour('home-requests'),
    ifMissing: 'skip',
  },
  {
    id: 'homeOvertime',
    chapter: 'home',
    target: tour('home-overtime'),
    ifMissing: 'skip',
  },

  /* ── tasks ──────────────────────────────────────────────────────────────────────────────── */
  {
    id: 'tasksSearch',
    chapter: 'tasks',
    route: (c) =>
      c.scopeOf('task.view') === 'workspace' && c.entitlements.menuTasks ? '/tasks' : '/my-tasks',
    before: view('list'),
    target: tour('list-search'),
  },
  { id: 'tasksViews', chapter: 'tasks', before: view('list'), target: tour('view-switch') },
  { id: 'tasksBoard', chapter: 'tasks', before: view('board'), target: TASKS_SCREEN },
  { id: 'tasksTimeline', chapter: 'tasks', before: view('timeline'), target: TASKS_SCREEN },
  {
    id: 'tasksNew',
    chapter: 'tasks',
    before: view('list'),
    target: tour('list-primary'),
    ifMissing: 'skip',
    when: (c) => c.has('task.create'),
  },

  /* ── a task, open ───────────────────────────────────────────────────────────────────────── */
  { id: 'taskOpen', chapter: 'task', before: OPEN_TASK, target: '[role="dialog"]' },
  {
    id: 'taskTitle',
    chapter: 'task',
    before: OPEN_TASK,
    target: tour('task-title'),
    ifMissing: 'center',
  },
  {
    id: 'taskProperties',
    chapter: 'task',
    before: OPEN_TASK,
    target: tour('task-properties'),
    side: 'left',
  },
  {
    id: 'taskLineItem',
    chapter: 'task',
    before: OPEN_TASK,
    target: tour('task-line-item'),
    side: 'left',
    ifMissing: 'skip',
  },
  {
    id: 'taskEstimate',
    chapter: 'task',
    before: OPEN_TASK,
    target: tour('task-properties'),
    side: 'left',
  },
  {
    id: 'taskSubtasks',
    chapter: 'task',
    before: OPEN_TASK,
    target: tour('task-subtasks'),
    ifMissing: 'skip',
  },
  {
    id: 'taskDependencies',
    chapter: 'task',
    before: OPEN_TASK,
    target: tour('task-dependencies'),
    ifMissing: 'skip',
  },
  {
    id: 'taskAttachments',
    chapter: 'task',
    before: OPEN_TASK,
    target: tour('task-attachments'),
    ifMissing: 'skip',
  },
  {
    id: 'taskComments',
    chapter: 'task',
    /* ⚠️ The comments tab, picked: arriving back from the logs step, the logs tab is showing and
       the composer is not drawn at all. */
    before: [...OPEN_TASK, click('[id$="-tab-comments"]')],
    target: tour('task-comment'),
    ifMissing: 'skip',
  },
  {
    id: 'taskLogs',
    chapter: 'task',
    before: [...OPEN_TASK, click('[id$="-tab-logs"]')],
    target: tour('task-activity'),
    ifMissing: 'skip',
  },

  /* ── projects: every tab, clicked before it is described ───────────────────────────────── */
  {
    id: 'projectsList',
    chapter: 'projects',
    route: '/projects',
    target: '[data-screen="projects"] table',
    when: (c) => c.entitlements.menuProjects,
  },
  {
    id: 'projectOpen',
    chapter: 'projects',
    route: (c) => (c.projectId === null ? null : `/projects/${c.projectId}`),
    before: [projectTab('overview')],
    target: `${PROJECT} [role="tablist"]`,
  },
  { id: 'projectOverview', chapter: 'projects', before: [projectTab('overview')], target: PROJECT },
  { id: 'projectTasks', chapter: 'projects', before: [projectTab('tasks')], target: PROJECT },
  { id: 'projectTime', chapter: 'projects', before: [projectTab('time')], target: PROJECT },
  {
    id: 'projectContracts',
    chapter: 'projects',
    before: [projectTab('contracts')],
    target: PROJECT,
    /* 22 Oct 2026 · the contracts and the costs are behind the money, as on the page. */
    when: (c) => c.has('project.finance'),
  },
  {
    id: 'projectMoney',
    chapter: 'projects',
    before: [projectTab('finance')],
    target: PROJECT,
    /* 22 Oct 2026 · the tab's own gate (`project-screen.tsx`), not the report one. */
    when: (c) => c.has('project.finance'),
  },
  {
    id: 'projectCosts',
    chapter: 'projects',
    before: [projectTab('costs')],
    target: PROJECT,
    when: (c) => c.has('project.finance'),
  },
  {
    id: 'projectInvoices',
    chapter: 'projects',
    before: [projectTab('invoices')],
    target: PROJECT,
    when: (c) => c.has('project.finance'),
  },
  {
    id: 'projectResourcing',
    chapter: 'projects',
    before: [projectTab('resource')],
    target: PROJECT,
    /* 22 Oct 2026 · the resource plan is for whoever runs the project. */
    when: (c) => c.has('project.manage'),
  },
  { id: 'projectPeople', chapter: 'projects', before: [projectTab('people')], target: PROJECT },
  {
    id: 'projectAlerts',
    chapter: 'projects',
    before: [projectTab('alerts')],
    target: PROJECT,
    /* 22 Oct 2026 · the alerts and the history are for whoever runs the project. */
    when: (c) => c.has('project.manage'),
  },
  {
    id: 'projectEdit',
    chapter: 'projects',
    target: tour('project-actions'),
    side: 'left',
    ifMissing: 'skip',
    when: (c) => c.has('project.manage'),
  },

  /* ── time logging: how, then every view ─────────────────────────────────────────────────── */
  { id: 'timeStrip', chapter: 'time', route: '/time', target: tour('time-strip') },
  { id: 'timeDay', chapter: 'time', target: tour('time-day') },
  {
    id: 'timeTask',
    chapter: 'time',
    before: [click(tour('time-recent'))],
    target: tour('time-task'),
    ifMissing: 'skip',
  },
  { id: 'timeDuration', chapter: 'time', target: tour('worklog-duration'), ifMissing: 'skip' },
  { id: 'timeOvertime', chapter: 'time', target: tour('worklog-overtime'), ifMissing: 'skip' },
  { id: 'timeLog', chapter: 'time', target: tour('time-log'), ifMissing: 'skip' },
  {
    id: 'timeRecents',
    chapter: 'time',
    target: tour('time-recents'),
    side: 'left',
    ifMissing: 'skip',
  },
  { id: 'timeWeek', chapter: 'time', target: tour('time-week'), side: 'left', ifMissing: 'skip' },
  {
    id: 'logsList',
    chapter: 'time',
    route: '/time/logs',
    before: view('list'),
    target: '[data-screen="time-logs"]',
  },
  {
    id: 'logsTimesheet',
    chapter: 'time',
    before: view('timesheet'),
    target: '[data-screen="time-logs"]',
  },
  {
    id: 'logsCalendar',
    chapter: 'time',
    before: view('calendar'),
    target: '[data-screen="time-logs"]',
  },
  { id: 'logsGrid', chapter: 'time', before: view('grid'), target: '[data-screen="time-logs"]' },

  /* ── leave and overtime requests ────────────────────────────────────────────────────────── */
  {
    id: 'requestBalances',
    chapter: 'requests',
    route: '/requests',
    before: [LEAVE_TAB],
    target: tour('request-balances'),
    ifMissing: 'center',
    when: (c) => c.entitlements.leaveRequest,
  },
  {
    id: 'requestNew',
    chapter: 'requests',
    before: [LEAVE_TAB],
    target: tour('list-primary'),
    ifMissing: 'skip',
    when: (c) => c.entitlements.leaveRequest,
  },
  {
    id: 'requestCalendar',
    chapter: 'requests',
    before: OPEN_LEAVE_FORM,
    target: tour('leave-calendar'),
    ifMissing: 'skip',
    when: (c) => c.entitlements.leaveRequest,
  },
  {
    id: 'requestQuota',
    chapter: 'requests',
    before: OPEN_LEAVE_FORM,
    target: tour('leave-quota'),
    side: 'left',
    ifMissing: 'skip',
    when: (c) => c.entitlements.leaveRequest,
  },
  {
    id: 'requestNote',
    chapter: 'requests',
    before: OPEN_LEAVE_FORM,
    target: tour('leave-note'),
    ifMissing: 'skip',
    when: (c) => c.entitlements.leaveRequest,
  },
  {
    id: 'requestOvertime',
    chapter: 'requests',
    before: [click(`${REQUESTS} [id$="-tab-overtime"]`, true)],
    target: tour('list-primary'),
    ifMissing: 'skip',
    when: (c) => c.entitlements.leaveRequest,
  },
  {
    id: 'requestOpen',
    chapter: 'requests',
    before: [LEAVE_TAB, { click: `${REQUESTS} tbody tr`, required: true, opens: 'request' }],
    target: '[role="dialog"]',
    when: (c) => c.entitlements.leaveRequest,
  },
  {
    id: 'requestDecide',
    chapter: 'requests',
    route: '/to-decide',
    target: '[data-screen="to-decide"]',
    when: (c) => c.entitlements.leaveDecide,
  },

  /* ── contracts, invoices, people ────────────────────────────────────────────────────────── */
  {
    id: 'contractsList',
    chapter: 'contracts',
    route: '/contracts',
    target: '[data-screen="contracts"]',
    when: (c) => c.entitlements.menuContracts,
  },
  {
    id: 'invoicesList',
    chapter: 'contracts',
    route: '/invoices',
    target: '[data-screen="invoices"]',
    when: (c) => c.entitlements.menuInvoices,
  },
  { id: 'peopleList', chapter: 'people', route: '/people', target: '[data-screen="people"]' },
  {
    id: 'personOpen',
    chapter: 'people',
    before: [{ click: '[data-screen="people"] tbody tr', required: true, opens: 'person' }],
    target: '[data-screen="person"], [role="dialog"]',
  },

  /* ── reports, and the builder walked by building ────────────────────────────────────────── */
  { id: 'reportsList', chapter: 'reports', route: '/reports', target: '[data-screen="reports"]' },
  {
    id: 'reportSource',
    chapter: 'reports',
    route: '/reports/new',
    target: tour('report-source'),
    when: (c) => c.has('report.manage'),
  },
  {
    id: 'reportPeriod',
    chapter: 'reports',
    target: tour('report-period'),
    when: (c) => c.has('report.manage'),
  },
  {
    id: 'reportMetric',
    chapter: 'reports',
    before: HOURS,
    target: tour('report-metrics'),
    side: 'left',
    when: (c) => c.has('report.manage'),
  },
  {
    id: 'reportDimension',
    chapter: 'reports',
    before: [...HOURS, click(tour('report-dimensions-add')), click(tour('dimension-project'))],
    target: tour('report-dimensions'),
    side: 'left',
    when: (c) => c.has('report.manage'),
  },
  {
    id: 'reportDisplay',
    chapter: 'reports',
    target: tour('report-display'),
    side: 'left',
    when: (c) => c.has('report.manage'),
  },
  {
    id: 'reportFilters',
    chapter: 'reports',
    target: tour('report-filters'),
    side: 'left',
    when: (c) => c.has('report.manage'),
  },
  {
    id: 'reportSave',
    chapter: 'reports',
    target: tour('report-name'),
    when: (c) => c.has('report.manage'),
  },

  /* ── dashboards ─────────────────────────────────────────────────────────────────────────── */
  {
    id: 'dashLogging',
    chapter: 'dashboards',
    route: '/dashboards/logging',
    target: '[data-screen="dash-logging"]',
    when: (c) => c.entitlements.dashboardLogging,
  },
  {
    id: 'dashResource',
    chapter: 'dashboards',
    route: '/dashboards/resource',
    target: '[data-screen="dash-resource"]',
    when: (c) => c.entitlements.dashboardResource,
  },
  {
    id: 'dashLeave',
    chapter: 'dashboards',
    route: '/dashboards/leave',
    target: '[data-screen="dash-leave"]',
    when: (c) => c.entitlements.dashboardLeave,
  },

  /* ── the agent ──────────────────────────────────────────────────────────────────────────── */
  { id: 'agentComposer', chapter: 'agent', route: '/agent', target: tour('agent-composer') },
  { id: 'agentAttach', chapter: 'agent', target: tour('agent-attach'), ifMissing: 'skip' },
  { id: 'agentProposals', chapter: 'agent', target: null },
  { id: 'agentHistory', chapter: 'agent', target: tour('agent-history'), ifMissing: 'skip' },

  /* ── the end ────────────────────────────────────────────────────────────────────────────── */
  { id: 'end', chapter: 'end', target: null },
];

/** The steps this person gets, in order. Fixed when the tour starts. */
export function tourSteps(context: TourContext): TourStep[] {
  const open = new Set(CHAPTERS.filter((chapter) => chapter.when(context)).map((c) => c.id));
  const kept = (rule: StepRule): boolean =>
    open.has(rule.chapter) && (rule.when?.(context) ?? true);
  /* ⚠️ Over EVERY rule, before any is left out: a step's page is the page of the rule before it in
     the script, whether or not this person gets that rule — WITHIN ITS CHAPTER.
     ⚠️ (3 Nov 2026) Across a chapter boundary only a page this person gets carries over. The end
     card inherited `/agent` from the agent chapter on a plan without the agent, and the tour's last
     step loaded a screen that answered `403 feature_not_enabled`. */
  let page: string | null = null;
  let namedBy: StepRule | null = null;
  let lastKept: string | null = null;
  const pages = new Map<string, string | null>();
  for (const rule of RULES) {
    if (rule.route !== undefined) {
      page = typeof rule.route === 'function' ? rule.route(context) : rule.route;
      namedBy = rule;
    }
    const own: string | null =
      namedBy === null || kept(namedBy) || namedBy.chapter === rule.chapter ? page : lastKept;
    pages.set(rule.id, own);
    if (kept(rule)) lastKept = own;
  }
  return RULES.filter(kept).map((rule) => ({
    id: rule.id,
    chapter: rule.chapter,
    route: pages.get(rule.id) ?? null,
    before: rule.before ?? [],
    target: rule.target,
    side: rule.side ?? 'bottom',
    ifMissing: rule.ifMissing ?? 'center',
    title: `tour.${rule.id}.title` as MessageKey,
    body: `tour.${rule.id}.body` as MessageKey,
  }));
}

export function chapterOf(id: ChapterId): Chapter {
  return CHAPTERS.find((chapter) => chapter.id === id) as Chapter;
}
